import React from "react";

/**
 * Decorative banner illustration for Safety Checklists.
 * Features site safety inspections, scaffolding silhouettes, inspector figures
 * with clipboards, checklist checkmarks, and an architectural grid.
 */
export function SafetyChecklistBannerArt({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 1200 400"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="safetyChecklistSky" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FDE68A" />
          <stop offset="35%" stopColor="#34D399" />
          <stop offset="70%" stopColor="#059669" />
          <stop offset="100%" stopColor="#047857" />
        </linearGradient>
        <linearGradient id="safetyChecklistFade" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
          <stop offset="55%" stopColor="#FFFFFF" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect width="1200" height="400" fill="url(#safetyChecklistSky)" />

      {/* Sun glow */}
      <circle cx="1020" cy="90" r="95" fill="#ECFDF5" opacity="0.35" />

      {/* Blueprint grid */}
      <g stroke="#FFFFFF" strokeOpacity="0.13" strokeWidth="1">
        <line x1="680" y1="0" x2="680" y2="400" />
        <line x1="800" y1="0" x2="800" y2="400" />
        <line x1="920" y1="0" x2="920" y2="400" />
        <line x1="1040" y1="0" x2="1040" y2="400" />
        <line x1="1160" y1="0" x2="1160" y2="400" />
        <line x1="600" y1="80" x2="1200" y2="80" />
        <line x1="600" y1="160" x2="1200" y2="160" />
        <line x1="600" y1="240" x2="1200" y2="240" />
        <line x1="600" y1="320" x2="1200" y2="320" />
      </g>

      {/* Scaffolding & structure under inspection */}
      <g fill="#064E3B" opacity="0.38">
        <rect x="690" y="210" width="85" height="190" />
        <rect x="790" y="160" width="105" height="240" />
        <rect x="910" y="110" width="115" height="290" />
        <rect x="1040" y="180" width="110" height="220" />
      </g>

      {/* Scaffolding cross-bracing lines */}
      <g stroke="#FFFFFF" strokeWidth="1.5" strokeOpacity="0.22" fill="none">
        <line x1="790" y1="160" x2="895" y2="240" />
        <line x1="895" y1="160" x2="790" y2="240" />
        <line x1="790" y1="240" x2="895" y2="320" />
        <line x1="895" y1="240" x2="790" y2="320" />
        <line x1="910" y1="110" x2="1025" y2="200" />
        <line x1="1025" y1="110" x2="910" y2="200" />
        <line x1="910" y1="200" x2="1025" y2="290" />
        <line x1="1025" y1="200" x2="910" y2="290" />
      </g>

      {/* Tower crane atop structure */}
      <g stroke="#064E3B" strokeWidth="4" strokeLinecap="round" opacity="0.55" fill="none">
        <line x1="970" y1="400" x2="970" y2="60" />
        <line x1="970" y1="70" x2="1160" y2="70" />
        <line x1="970" y1="70" x2="920" y2="120" />
        <line x1="1130" y1="70" x2="1130" y2="140" />
      </g>

      {/* Translucent Checklist & Shield motif in midground */}
      <g transform="translate(860, 60)" opacity="0.32">
        {/* Shield outline */}
        <path
          d="M60 0 L110 20 L110 80 Q110 130 60 160 Q10 130 10 80 L10 20 Z"
          fill="#FFFFFF"
          fillOpacity="0.08"
          stroke="#FFFFFF"
          strokeWidth="3"
        />
        {/* Big checkmark inside shield */}
        <path
          d="M38 78 L52 92 L84 56"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* Inspector figures with clipboard & hard hat */}
      <g transform="translate(740,400)" fill="#064E3B" opacity="0.9">
        {/* Body */}
        <path d="M0 0 L0 -100 Q0 -115 15 -115 L45 -115 Q60 -115 60 -100 L60 0 Z" />
        {/* Head */}
        <circle cx="30" cy="-135" r="17" />
        {/* Hard hat */}
        <path d="M10 -147 Q30 -165 50 -147 L54 -137 L6 -137 Z" />
        {/* Clipboard held in hand */}
        <rect x="42" y="-95" width="22" height="34" rx="2" fill="#FFFFFF" opacity="0.85" />
        <rect x="47" y="-99" width="12" height="5" rx="1" fill="#D1D5DB" />
        {/* Checklist lines on board */}
        <line x1="46" y1="-85" x2="58" y2="-85" stroke="#059669" strokeWidth="2" />
        <line x1="46" y1="-77" x2="58" y2="-77" stroke="#059669" strokeWidth="2" />
        <line x1="46" y1="-69" x2="54" y2="-69" stroke="#059669" strokeWidth="2" />
      </g>

      {/* Second engineer pointing at structure */}
      <g transform="translate(820,400) scale(0.92)" fill="#064E3B" opacity="0.88">
        <path d="M0 0 L0 -95 Q0 -110 15 -110 L45 -110 Q60 -110 60 -95 L60 0 Z" />
        <circle cx="30" cy="-130" r="17" />
        <path d="M10 -142 Q30 -160 50 -142 L54 -132 L6 -132 Z" />
      </g>

      {/* Fade overlay for text legibility */}
      <rect width="1200" height="400" fill="url(#safetyChecklistFade)" />
    </svg>
  );
}

/**
 * Decorative banner illustration for Permit To Work.
 * Features high-risk work authorization, industrial towers, cranes,
 * harness workers at height, authorization lock shield, and safety demarcation.
 */
export function PermitBannerArt({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 1200 400"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="permitSky" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FED7AA" />
          <stop offset="40%" stopColor="#F59E0B" />
          <stop offset="75%" stopColor="#D97706" />
          <stop offset="100%" stopColor="#9A3412" />
        </linearGradient>
        <linearGradient id="permitFade" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
          <stop offset="55%" stopColor="#FFFFFF" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect width="1200" height="400" fill="url(#permitSky)" />

      {/* Sun / High-risk radiant glow */}
      <circle cx="1010" cy="85" r="100" fill="#FFFBEB" opacity="0.38" />

      {/* Blueprint grid */}
      <g stroke="#FFFFFF" strokeOpacity="0.13" strokeWidth="1">
        <line x1="680" y1="0" x2="680" y2="400" />
        <line x1="800" y1="0" x2="800" y2="400" />
        <line x1="920" y1="0" x2="920" y2="400" />
        <line x1="1040" y1="0" x2="1040" y2="400" />
        <line x1="1160" y1="0" x2="1160" y2="400" />
        <line x1="600" y1="80" x2="1200" y2="80" />
        <line x1="600" y1="160" x2="1200" y2="160" />
        <line x1="600" y1="240" x2="1200" y2="240" />
        <line x1="600" y1="320" x2="1200" y2="320" />
      </g>

      {/* Industrial towers & framework */}
      <g fill="#7C2D12" opacity="0.36">
        <rect x="710" y="220" width="80" height="180" />
        <rect x="810" y="160" width="100" height="240" />
        <rect x="930" y="110" width="120" height="290" />
        <rect x="1070" y="190" width="90" height="210" />
      </g>

      {/* Lattice structural tower lines */}
      <g stroke="#FFFFFF" strokeWidth="1.5" strokeOpacity="0.2" fill="none">
        <line x1="710" y1="220" x2="790" y2="300" />
        <line x1="790" y1="220" x2="710" y2="300" />
        <line x1="930" y1="110" x2="1050" y2="210" />
        <line x1="1050" y1="110" x2="930" y2="210" />
      </g>

      {/* Heavy crane with suspended load */}
      <g stroke="#431407" strokeWidth="4" strokeLinecap="round" opacity="0.55" fill="none">
        <line x1="990" y1="400" x2="990" y2="55" />
        <line x1="990" y1="65" x2="1180" y2="65" />
        <line x1="990" y1="65" x2="940" y2="115" />
        <line x1="1140" y1="65" x2="1140" y2="155" />
        {/* Suspended permit load beam */}
        <line x1="1120" y1="155" x2="1160" y2="155" strokeWidth="6" />
      </g>

      {/* Stylized Permit Lock / Security Seal in sky */}
      <g transform="translate(860, 50)" opacity="0.3">
        {/* Padlock shackle */}
        <path
          d="M35 50 L35 30 Q35 10 60 10 Q85 10 85 30 L85 50"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="6"
          strokeLinecap="round"
        />
        {/* Padlock body */}
        <rect x="20" y="50" width="80" height="60" rx="10" fill="#FFFFFF" fillOpacity="0.15" stroke="#FFFFFF" strokeWidth="3" />
        {/* Keyhole */}
        <circle cx="60" cy="74" r="6" fill="#FFFFFF" />
        <polygon points="57,74 63,74 65,92 55,92" fill="#FFFFFF" />
      </g>

      {/* Worker at height with harness & safety lanyard */}
      <g transform="translate(930, 200) scale(0.65)" fill="#431407" opacity="0.9">
        <rect x="-30" y="60" width="80" height="8" fill="#431407" />
        <path d="M0 60 L0 -20 Q0 -35 15 -35 L35 -35 Q50 -35 50 -20 L50 60 Z" />
        <circle cx="25" cy="-55" r="14" />
        <path d="M5 -65 Q25 -80 45 -65 L48 -58 L2 -58 Z" />
        {/* Safety lifeline rope to structure */}
        <path d="M25 -10 Q50 -20 70 -80" fill="none" stroke="#F59E0B" strokeWidth="4" strokeLinecap="round" />
      </g>

      {/* Authorized supervisor figure at bottom */}
      <g transform="translate(760,400)" fill="#431407" opacity="0.88">
        <path d="M0 0 L0 -95 Q0 -110 15 -110 L45 -110 Q60 -110 60 -95 L60 0 Z" />
        <circle cx="30" cy="-130" r="17" />
        <path d="M10 -142 Q30 -160 50 -142 L54 -132 L6 -132 Z" />
      </g>
      <g transform="translate(830,400) scale(0.92)" fill="#431407" opacity="0.88">
        <path d="M0 0 L0 -95 Q0 -110 15 -110 L45 -110 Q60 -110 60 -95 L60 0 Z" />
        <circle cx="30" cy="-130" r="17" />
        <path d="M10 -142 Q30 -160 50 -142 L54 -132 L6 -132 Z" />
      </g>

      {/* Safety cones silhouette */}
      <g transform="translate(710, 370)" fill="#EA580C" opacity="0.85">
        <polygon points="12,30 28,30 22,0 18,0" />
        <rect x="8" y="27" width="24" height="3" rx="1" />
        <rect x="14" y="16" width="12" height="4" fill="#FFFFFF" opacity="0.9" />
      </g>

      {/* Fade overlay for text legibility */}
      <rect width="1200" height="400" fill="url(#permitFade)" />
    </svg>
  );
}

/**
 * Decorative banner illustration for Safety Observations.
 * Features hazard awareness beacon, alert rays, warning triangle silhouette,
 * structural site framework, and safety engineers inspecting hazard zones.
 */
export function SafetyObservationBannerArt({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 1200 400"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="observationSky" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FED7AA" />
          <stop offset="35%" stopColor="#FB923C" />
          <stop offset="70%" stopColor="#EA580C" />
          <stop offset="100%" stopColor="#991B1B" />
        </linearGradient>
        <linearGradient id="observationFade" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
          <stop offset="55%" stopColor="#FFFFFF" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect width="1200" height="400" fill="url(#observationSky)" />

      {/* Hazard awareness spotlight ray & sun orb */}
      <circle cx="1025" cy="88" r="95" fill="#FEF3C7" opacity="0.38" />
      <polygon points="1025,88 800,400 1200,400" fill="#FFF7ED" opacity="0.08" />

      {/* Blueprint grid */}
      <g stroke="#FFFFFF" strokeOpacity="0.13" strokeWidth="1">
        <line x1="680" y1="0" x2="680" y2="400" />
        <line x1="800" y1="0" x2="800" y2="400" />
        <line x1="920" y1="0" x2="920" y2="400" />
        <line x1="1040" y1="0" x2="1040" y2="400" />
        <line x1="1160" y1="0" x2="1160" y2="400" />
        <line x1="600" y1="80" x2="1200" y2="80" />
        <line x1="600" y1="160" x2="1200" y2="160" />
        <line x1="600" y1="240" x2="1200" y2="240" />
        <line x1="600" y1="320" x2="1200" y2="320" />
      </g>

      {/* Skyline under observation */}
      <g fill="#450A0A" opacity="0.38">
        <rect x="700" y="225" width="85" height="175" />
        <rect x="800" y="165" width="110" height="235" />
        <rect x="925" y="115" width="115" height="285" />
        <rect x="1055" y="195" width="95" height="205" />
      </g>

      {/* Tower crane */}
      <g stroke="#450A0A" strokeWidth="4" strokeLinecap="round" opacity="0.55" fill="none">
        <line x1="980" y1="400" x2="980" y2="65" />
        <line x1="980" y1="75" x2="1165" y2="75" />
        <line x1="980" y1="75" x2="930" y2="125" />
        <line x1="1135" y1="75" x2="1135" y2="145" />
      </g>

      {/* Translucent Hazard Warning Triangle Emblem in midground */}
      <g transform="translate(850, 55)" opacity="0.32">
        {/* Warning Triangle */}
        <path
          d="M60 10 L115 105 L5 105 Z"
          fill="#FFFFFF"
          fillOpacity="0.12"
          stroke="#FFFFFF"
          strokeWidth="4"
          strokeLinejoin="round"
        />
        {/* Exclamation point */}
        <line x1="60" y1="42" x2="60" y2="72" stroke="#FFFFFF" strokeWidth="6" strokeLinecap="round" />
        <circle cx="60" cy="88" r="3.5" fill="#FFFFFF" />
      </g>

      {/* Two safety observers inspecting and pointing at site condition */}
      <g transform="translate(750,400)" fill="#1F2937" opacity="0.9">
        <path d="M0 0 L0 -95 Q0 -110 15 -110 L45 -110 Q60 -110 60 -95 L60 0 Z" />
        <circle cx="30" cy="-130" r="17" />
        <path d="M10 -142 Q30 -160 50 -142 L54 -132 L6 -132 Z" />
        {/* Arm gesturing forward */}
        <path d="M45 -95 Q65 -90 85 -105" stroke="#1F2937" strokeWidth="9" strokeLinecap="round" fill="none" />
      </g>
      <g transform="translate(840,400) scale(0.92)" fill="#1F2937" opacity="0.88">
        <path d="M0 0 L0 -95 Q0 -110 15 -110 L45 -110 Q60 -110 60 -95 L60 0 Z" />
        <circle cx="30" cy="-130" r="17" />
        <path d="M10 -142 Q30 -160 50 -142 L54 -132 L6 -132 Z" />
        {/* Clipboard held by observer */}
        <rect x="35" y="-85" width="20" height="30" rx="2" fill="#FFFFFF" opacity="0.85" />
      </g>

      {/* Fade overlay for text legibility */}
      <rect width="1200" height="400" fill="url(#observationFade)" />
    </svg>
  );
}

/**
 * Reusable Hero Banner Component that displays the beautiful background artwork,
 * gradient overlay, category tag, title, description, and action buttons.
 */
export function SafetyHeroBanner({
  type = "checklist", // "checklist" | "permit" | "observation"
  categoryTag = "SAFETY MANAGEMENT",
  categoryIcon: CategoryIcon,
  title,
  subtitle,
  children,
  className = "",
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-slate-200 shadow-sm bg-white ${className}`}
    >
      {type === "checklist" && (
        <SafetyChecklistBannerArt className="absolute inset-0 h-full w-full object-cover" />
      )}
      {type === "permit" && (
        <PermitBannerArt className="absolute inset-0 h-full w-full object-cover" />
      )}
      {type === "observation" && (
        <SafetyObservationBannerArt className="absolute inset-0 h-full w-full object-cover" />
      )}

      {/* Subtle white gradient overlay ensuring high contrast and legibility */}
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/15 pointer-events-none" />

      {/* Banner content */}
      <div className="relative flex flex-wrap items-end justify-between gap-6 px-6 py-7 md:px-10 md:py-10">
        <div className="min-w-0 max-w-xl">
          {categoryTag && (
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-orange-600 font-bold mb-2">
              {CategoryIcon && (
                <CategoryIcon className="w-3.5 h-3.5 text-orange-600 shrink-0" />
              )}
              <span>{categoryTag}</span>
            </div>
          )}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-display font-extrabold tracking-tight text-slate-900 break-words">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm md:text-base text-slate-600 mt-2 max-w-md">
              {subtitle}
            </p>
          )}
        </div>

        {children && (
          <div className="relative z-10 flex flex-wrap items-center gap-2.5 sm:gap-3">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

export default SafetyHeroBanner;
